import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const THEME_TRANSITION_MS = 200;

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  const applyTheme = useCallback((isDark, animate = false) => {
    const root = document.documentElement;

    if (animate) {
      root.classList.add('theme-transitioning');
      window.clearTimeout(window.__themeTransitionTimer);
      // Commit transition rules before changing `dark`; otherwise both class
      // changes may land in one paint and the browser can skip the animation.
      void root.offsetWidth;
    }

    root.classList.toggle('dark', isDark);
    root.style.colorScheme = isDark ? 'dark' : 'light';
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    // Keep the old key for installations that still read it.
    localStorage.setItem('darkMode', JSON.stringify(isDark));

    const themeColor = document.querySelector('meta[name="theme-color"]');
    themeColor?.setAttribute('content', isDark ? '#020617' : '#f8fafc');

    if (animate) {
      window.__themeTransitionTimer = window.setTimeout(() => {
        root.classList.remove('theme-transitioning');
      }, THEME_TRANSITION_MS + 50);
    }
  }, []);

  useEffect(() => {
    const syncThemeAcrossTabs = (event) => {
      if (event.key !== 'theme' || !event.newValue) return;
      const nextDarkMode = event.newValue === 'dark';
      applyTheme(nextDarkMode, true);
      setDarkMode(nextDarkMode);
    };

    window.addEventListener('storage', syncThemeAcrossTabs);
    return () => window.removeEventListener('storage', syncThemeAcrossTabs);
  }, [applyTheme]);

  const toggleDarkMode = useCallback(() => {
    const nextDarkMode = !document.documentElement.classList.contains('dark');
    applyTheme(nextDarkMode, true);
    setDarkMode(nextDarkMode);
  }, [applyTheme]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
